import { useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CollegePicker } from '@backrs_ui/grow-ui/src/components/MyColleges/CollegePicker';
import { LineInfoIcon } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/LineInfoIcon';
import { ViewToggle } from '@backrs_ui/grow-ui/src/pages/MyColleges/components/ViewToggle';
import type { CollegePick } from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';
import {
  MAX_COMPARISON_COLLEGES,
  UserCollege,
} from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';

import { Box, Button, SxProps, Theme, Typography } from '@mui/material';

import { NameDialog } from './NameDialog';

const STRINGS = appStrings.modules.myColleges.compare;

export type CompareView = 'heatMap' | 'cards';

const VIEW_OPTIONS = [
  { value: 'heatMap', label: STRINGS.views.heatMap, icon: 'LayoutGrid02' },
  { value: 'cards', label: STRINGS.views.cards, icon: 'Grid01' },
] as const;

type ComparisonControlsProps = Readonly<{
  colleges: readonly UserCollege[];
  picked: readonly CollegePick[];
  onPickedChange: (picked: readonly CollegePick[]) => void;
  isSaved: boolean;
  canSave: boolean;
  saving: boolean;
  saveFailed: boolean;
  onSave: (name: string) => Promise<boolean>;
  onUpdate: () => void;
  canCompare: boolean;
  onCompare: () => void;
  canEditWeights: boolean;
  onEditWeights: () => void;
  view: CompareView;
  onViewChange: ((view: CompareView) => void) | null;
}>;

export const ComparisonControls = ({
  colleges,
  picked,
  onPickedChange,
  isSaved,
  canSave,
  saving,
  saveFailed,
  onSave,
  onUpdate,
  canCompare,
  onCompare,
  canEditWeights,
  onEditWeights,
  view,
  onViewChange,
}: ComparisonControlsProps) => {
  const [naming, setNaming] = useState(false);

  return (
    <Box sx={containerSx}>
      <Box sx={labelSx}>
        <Typography variant="text-sm-medium" sx={labelTextSx}>
          {STRINGS.search.label}
        </Typography>
        <LineInfoIcon
          idPrefix="compare-search"
          iconName="HelpCircle"
          iconColor="Gray/400"
          info={{ kind: 'tooltip', text: STRINGS.search.help }}
        />
      </Box>
      <Box sx={rowSx}>
        <CollegePicker
          idPrefix="compare"
          colleges={colleges}
          picked={picked}
          onPickedChange={onPickedChange}
          strings={{ ...STRINGS.search, full: STRINGS.search.full(MAX_COMPARISON_COLLEGES) }}
          max={MAX_COMPARISON_COLLEGES}
          searchSx={searchSx}
        >
          <Button
            id="compare-run"
            hierarchy="Primary"
            size="lg"
            disabled={!canCompare}
            onClick={onCompare}
          >
            {STRINGS.compareButton}
          </Button>
          <Button
            id="compare-save-comparison"
            hierarchy="Secondary color"
            size="lg"
            disabled={!canSave || saving}
            onClick={isSaved ? onUpdate : () => setNaming(true)}
          >
            {isSaved ? STRINGS.update : STRINGS.save}
          </Button>
        </CollegePicker>
        <Box sx={actionsSx}>
          <Button
            id="compare-edit-weights"
            hierarchy="Secondary gray"
            size="md"
            disabled={!canEditWeights}
            onClick={onEditWeights}
          >
            {STRINGS.weights.editButton}
          </Button>
          {onViewChange && (
            <ViewToggle
              id="compare-view"
              options={VIEW_OPTIONS}
              value={view}
              onChange={onViewChange}
            />
          )}
        </Box>
      </Box>

      {naming && (
        <NameDialog
          id="compare-save-dialog"
          title={STRINGS.saveDialogTitle}
          saving={saving}
          failed={saveFailed}
          onClose={() => setNaming(false)}
          onSubmit={async (name) => {
            if (await onSave(name)) setNaming(false);
          }}
        />
      )}
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const labelSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xxs'],
});

const labelTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const rowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-lg'],
});

const searchSx: SxProps<Theme> = {
  display: 'flex',
  width: { xs: '100%', sm: '415px' },
};

const actionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});
